import type { Fiat } from "~/types/global.types";
import { convertCurrency } from "./convertCurrency";

/** USD-normalized fiat rates per day, keyed by currency symbol, sorted ascending by day. */
export type FiatRatePoint = { day: string; rate: number };
export type FiatRateHistory = Record<string, FiatRatePoint[]>;

function toDayString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Returns the historical rate for a currency closest at/just-before the target
 * day. Falls back to the earliest available point if the target predates all
 * history, or `null` when no history exists for the symbol.
 */
export function getRateOnDate(
  history: FiatRateHistory | undefined,
  symbol: string,
  date: Date
): number | null {
  const points = history?.[symbol];
  if (!points || points.length === 0) return null;
  const target = toDayString(date);

  let match: FiatRatePoint | null = null;
  for (const point of points) {
    if (point.day <= target) match = point;
    else break;
  }
  // Target predates all stored history -> use earliest point as best effort.
  return (match ?? points[0]).rate;
}

/**
 * Date-aware currency conversion. When a historical rate is available for both
 * the source and target currency at `date`, uses the ratio of historical rates;
 * otherwise falls back to the current spot conversion via {@link convertCurrency}.
 */
export function convertCurrencyOnDate(
  amount: number,
  toCurrency: Fiat,
  fromCurrency: Fiat,
  date: Date | undefined,
  history?: FiatRateHistory
): number {
  if (date && history) {
    const toRate = getRateOnDate(history, toCurrency?.value, date);
    const fromRate = getRateOnDate(history, fromCurrency?.value, date);
    if (toRate != null && fromRate != null && fromRate !== 0) {
      return (toRate / fromRate) * amount;
    }
  }
  return convertCurrency(amount, toCurrency, fromCurrency);
}
